/**
 * Free guide cart sync.
 *
 * Keeps the bonus "7 Training & Nutrition E-guides" product in step with the
 * cart everywhere (cart page, Zipify slide cart, PDP ajax add, third-party
 * widgets) by reacting to every cart mutation rather than relying on a
 * full /cart page render.
 *
 * Rules:
 *   - paid items present and guide missing -> add guide (qty 1)
 *   - no paid items (cart empty or guide-only) -> remove guide
 *   - guide present with qty != 1 -> clamp back to 1
 */
(function () {
  'use strict';

  var FREE_GUIDE_PRODUCT_ID = 6725535170767;
  var FREE_GUIDE_VARIANT_ID = 40006750503119;
  var SYNC_DEBOUNCE_MS = 250;
  var SYNC_BLOCK_TIMEOUT_MS = 3000;

  var routeRoot =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    '/';

  // Captured before we install our wrapper, so internal requests bypass our
  // own hook (avoids recursion and keeps our calls invisible to ourselves
  // while still flowing through any third-party fetch wrappers).
  var nativeFetch =
    typeof window.fetch === 'function' ? window.fetch.bind(window) : null;

  var syncing = false;
  var debounceTimer = null;

  function isCartMutationUrl(url) {
    if (!url) return false;
    try {
      return /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/i.test(
        String(url)
      );
    } catch (_) {
      return false;
    }
  }

  function internalRequest(path, options) {
    if (!nativeFetch) return Promise.reject(new Error('fetch unavailable'));
    var opts = options || {};
    opts.credentials = 'same-origin';
    opts.headers = Object.assign(
      { 'Content-Type': 'application/json', Accept: 'application/json' },
      opts.headers || {}
    );
    return nativeFetch(routeRoot + path, opts).then(function (r) {
      if (!r.ok) throw new Error('cart request failed: ' + r.status);
      return r.json();
    });
  }

  function getCart() {
    return internalRequest('cart.js', { method: 'GET' });
  }
  function addGuide() {
    return internalRequest('cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ id: FREE_GUIDE_VARIANT_ID, quantity: 1 }),
    });
  }
  function changeLine(lineKey, quantity) {
    return internalRequest('cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    });
  }

  function notifyCartUpdated() {
    try {
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { source: 'free-guide-cart' },
        })
      );
    } catch (_) {}
  }

  function isOnCartPage() {
    try {
      var path = window.location.pathname.replace(/\/+$/, '');
      return path === routeRoot.replace(/\/+$/, '') + '/cart' || path === '/cart';
    } catch (_) {
      return false;
    }
  }

  function syncFreeGuide() {
    if (syncing) return Promise.resolve();
    syncing = true;
    var changed = false;
    return getCart()
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return null;

        var guideLine = null;
        var paidQty = 0;
        for (var i = 0; i < cart.items.length; i++) {
          var item = cart.items[i];
          var isGuide =
            item.product_id === FREE_GUIDE_PRODUCT_ID ||
            item.variant_id === FREE_GUIDE_VARIANT_ID;
          if (isGuide) {
            guideLine = item;
          } else {
            paidQty += item.quantity || 0;
          }
        }

        function markChanged() {
          changed = true;
          notifyCartUpdated();
        }

        if (paidQty === 0) {
          if (guideLine) {
            return changeLine(guideLine.key, 0).then(markChanged);
          }
          return null;
        }
        if (!guideLine) {
          return addGuide().then(markChanged);
        }
        if (guideLine.quantity !== 1) {
          return changeLine(guideLine.key, 1).then(markChanged);
        }
        return null;
      })
      .catch(function (err) {
        try {
          console.warn('[free-guide-cart] sync failed', err);
        } catch (_) {}
      })
      .then(function () {
        syncing = false;
        // The /cart page renders line items via server-side Liquid and has
        // no live re-render, so when our sync actually mutates the cart we
        // need to refresh the page for the UI to match the new state.
        if (changed && isOnCartPage()) {
          try {
            window.location.reload();
          } catch (_) {}
        }
      });
  }

  function scheduleSync() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(syncFreeGuide, SYNC_DEBOUNCE_MS);
  }

  // Cap the blocking window so a slow sync can never freeze a third-party
  // drawer's add-to-cart flow.
  function withTimeout(promise, ms) {
    return Promise.race([
      promise,
      new Promise(function (resolve) {
        setTimeout(resolve, ms);
      }),
    ]);
  }

  // Intercept third-party cart mutations and run our sync BEFORE returning
  // the response. Drawers that fetch the cart immediately after their own
  // add-to-cart (e.g. Zipify slide cart) will then see the free guide on
  // first render rather than after a debounced refresh.
  if (nativeFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url;
      var promise = nativeFetch(input, init);
      if (!isCartMutationUrl(url)) return promise;
      return promise.then(function (response) {
        if (!response || !response.ok) return response;
        return withTimeout(syncFreeGuide(), SYNC_BLOCK_TIMEOUT_MS).then(
          function () {
            return response;
          }
        );
      });
    };
  }

  // XHR fallback — cannot delay delivery to the caller, only schedule an
  // async sync after the response lands.
  if (window.XMLHttpRequest) {
    var XHRProto = XMLHttpRequest.prototype;
    var originalOpen = XHRProto.open;
    var originalSend = XHRProto.send;
    XHRProto.open = function (method, url) {
      this.__freeGuideUrl = url;
      return originalOpen.apply(this, arguments);
    };
    XHRProto.send = function () {
      if (isCartMutationUrl(this.__freeGuideUrl)) {
        this.addEventListener('loadend', function () {
          if (this.status >= 200 && this.status < 300) scheduleSync();
        });
      }
      return originalSend.apply(this, arguments);
    };
  }

  document.addEventListener('cart:updated', function (event) {
    if (event && event.detail && event.detail.source === 'free-guide-cart') {
      return;
    }
    scheduleSync();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync);
  } else {
    scheduleSync();
  }
})();
